import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)

export const TIMESTAMP_SURROUND = { value: 1, unit: "hour" }
export const CUSTOM_TIMEFRAME = "custom"
export const TIMEFRAMES = {
  R1H: { key: "R1H", value: 1, unit: "hour", label: "1H", pollInterval: 60000 },
  R4H: { key: "R4H", value: 4, unit: "hour", label: "4H", pollInterval: 60000 },
  R8H: { key: "R8H", value: 8, unit: "hour", label: "8H", pollInterval: 60000 },
  R12H: { key: "R12H", value: 12, unit: "hour", label: "12H", pollInterval: 2 * 60000 },
  R24H: { key: "R24H", value: 24, unit: "hour", label: "24H", pollInterval: 2 * 60000 },
  R48H: { key: "R48H", value: 48, unit: "hour", label: "48H", pollInterval: 2 * 60000 },
  R7D: { key: "R7D", value: 7, unit: "days", label: "7D", pollInterval: 5 * 60000 },
  R30D: { key: "R30D", value: 30, unit: "days", label: "30D", pollInterval: 5 * 60000 },
}

export class Timeframes {
  static timeframeFromParam(timeframe, fallback = "R4H", timeframes = TIMEFRAMES) {
    return (
      timeframes[timeframe] ||
      Object.values(timeframes).find((range) => {
        return range.label == timeframe
      }) ||
      timeframes[fallback]
    )
  }

  static backwardsCompatibleTimeframeKey(timeframe) {
    if (timeframe == CUSTOM_TIMEFRAME || timeframe == null) {
      return null
    }
    return this.timeframeFromParam(timeframe).key
  }

  static isValidTimeframe(timeframe) {
    return this.backwardsCompatibleTimeframeKey(timeframe) !== null
  }

  static longest(timeframes = TIMEFRAMES) {
    const now = dayjs()
    const longest = Object.values(timeframes).reduce((widest, timeframe) =>
      now.add(timeframe.value, timeframe.unit).isAfter(now.add(widest.value, widest.unit))
        ? timeframe
        : widest,
    )

    return longest.key
  }

  static now() {
    return dayjs().utc().second(0).millisecond(0).toISOString()
  }

  static fromToFromTimeframe(param, fallback = "R4H", timeframes = TIMEFRAMES) {
    const timeframe = this.timeframeFromParam(param, fallback, timeframes)
    return {
      from: dayjs()
        .subtract(timeframe.value, timeframe.unit)
        .utc()
        .second(0)
        .millisecond(0)
        .toISOString(),
      to: this.now(),
    }
  }

  static clampWindow({ start, end }) {
    const longest = this.fromToFromTimeframe(this.longest())
    const span = Date.parse(longest.to) - Date.parse(longest.from)
    const earliest = new Date(Date.parse(end) - span).toISOString()

    return { start: Date.parse(start) > Date.parse(earliest) ? start : earliest, end }
  }

  static pollIntervalFromTimeframe(timeframe) {
    return this.timeframeFromParam(timeframe, null)?.pollInterval || 0
  }

  static timeframeFromTimestamp(timestamp) {
    for (const timeframe of TIMEFRAMES) {
      if (dayjs(timestamp).isAfter(dayjs().subtract(timeframe.value, timeframe.unit))) {
        return timeframe
      }
    }
  }

  static fromToFromTimestamp(timestamp) {
    const { value, unit } = TIMESTAMP_SURROUND

    return {
      from: dayjs(timestamp).subtract(value, unit).utc().toISOString(),
      to: dayjs(timestamp).add(value, unit).utc().toISOString(),
    }
  }

  static humanReadableTimeframe(timeframe, from, to) {
    if (timeframe === "custom") {
      if (!from || !to) return "custom range"

      const start = dayjs(from)
      const end = dayjs(to)
      const diffInHours = end.diff(start, "hours")
      const diffInDays = Math.ceil(end.diff(start, "days", true))

      // If less than 24 hours, show in hours
      if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"}`
      }

      // Otherwise show in days (rounded up for better UX)
      return `${diffInDays} ${diffInDays === 1 ? "day" : "days"}`
    }

    const tf = this.timeframeFromParam(timeframe)
    if (!tf) return null

    const { value, key, unit } = tf
    // "R1H" is the only singular case, all others should be plural
    const displayUnit = key === "R1H" ? "hour" : unit.endsWith("s") ? unit : `${unit}s`
    return `recent ${value} ${displayUnit}`
  }
}

export default Timeframes
